import { useSetRecoilState } from 'recoil';
import { ApiUrl, Constants } from '@/utils/constants';
import useSetLoading from '@/hooks/useSetLoading';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { DeviceDeliCollInfoResponse } from '@/types/api/mypage/deviceDeliCollInfo';
import { DeviceDeliCollInfoState } from '@/states/deviceDeliCollInfoState';
import alertState from '@/states/alertState';

/** 機器配送回収情報取得APIのURL */
const apiUrl = ApiUrl.Read.DEVICE_DELI_COLL_INFO;

/**
 * 機器配送回収情報取得フック。
 * レンタル機器の配送・返却状況を取得し、確認画面用stateへ保存する。
 *
 * @returns execute - API実行関数
 */
const useGetDeviceDeliCollInfo = () => {
  const setDeliCollInfo = useSetRecoilState(DeviceDeliCollInfoState);
  const setAlert = useSetRecoilState(alertState);

  // hooks
  const { startLoading, finishLoading } = useSetLoading();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();

  /**
   * API実行関数
   */
  const execute = useCallback(async () => {
    // 機器配送回収情報は契約サービス単位で管理されるため、serviceId付きURLへ変換する。
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
    const headers = await getSigV4HeadersForGet(apiUrlWithServiceId);

    startLoading(apiUrlWithServiceId);

    await api
      .get(apiUrlWithServiceId, headers)
      .then((res: AxiosResponse<DeviceDeliCollInfoResponse>) => {
        // 配送回収の確認画面で再利用するため、取得結果をRecoilへ保持する。
        setDeliCollInfo(res.data);
      })
      .catch((error: any) => {
        // ログイントークンの有効期限が切れていたら、ログイン画面に遷移
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }
        // 失敗時は個別処理せず共通アラートで通知する。
        setAlert({
          isOpen: true,
          apiType: Constants.ApiType.API_GET,
        });
      })
      .finally(() => finishLoading(apiUrlWithServiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useGetDeviceDeliCollInfo;
